import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy,
  TrendingUp,
  Users,
  Layers,
  Sparkles,
  Search,
  ArrowLeft,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildOgImageUrl } from '../utils/ogImage';
import LeaderboardPodium from '../components/leaderboard/LeaderboardPodium';
import CategoryChampions from '../components/leaderboard/CategoryChampions';
import LeaderboardRankList from '../components/leaderboard/LeaderboardRankList';
import {
  LEADER_METRICS,
  getCategoryChampions,
  leagueStats,
  rankForums,
  type LeaderMetric,
} from '../utils/leaderboard';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

const LeaderboardPage: React.FC<Props> = ({ darkMode }) => {
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [metric, setMetric] = useState<LeaderMetric>('score');
  const [loading, setLoading] = useState(true);

  const ranked = useMemo(() => rankForums(forums, metric, 50), [forums, metric]);
  const topThree = ranked.slice(0, 3);

  const pageSeo = useMemo(
    () => ({
      title: 'Forum Ligi — Podyum, Kategori Şampiyonları ve Lig Kademeleri',
      description:
        'Türk forumlarının lig tablosu: podyum, kategori birincileri, S/A/B lig kademeleri ve büyüme sıralaması.',
      keywords: 'forum ligi, podyum, kategori şampiyonu, forum sıralaması, büyüme ligi',
      canonical: '/leaderboard',
      ogImage:
        topThree.length >= 1
          ? buildOgImageUrl({
              title: 'Forum Ligi',
              subtitle: `${forums.length} aktif forum`,
              line1: topThree[0] ? `#1 ${topThree[0].forum.name}` : undefined,
              line2: topThree[1] ? `#2 ${topThree[1].forum.name}` : undefined,
              line3: topThree[2] ? `#3 ${topThree[2].forum.name}` : undefined,
              badge: 'Podyum',
            })
          : undefined,
    }),
    [forums.length, metric, topThree]
  );

  usePageSeo(pageSeo, [pageSeo]);

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => {
        setForums(data || []);
        setLoading(false);
      });
  }, []);

  const champions = useMemo(() => getCategoryChampions(forums, metric), [forums, metric]);
  const stats = useMemo(() => leagueStats(forums), [forums]);

  const pageBg = darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900';
  const heroBg = darkMode
    ? 'bg-gradient-to-br from-indigo-950 via-gray-900 to-amber-950/30 border-indigo-900/40'
    : 'bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-800 border-indigo-500/30';
  const statBox = darkMode
    ? 'rounded-xl border p-3 min-w-0 bg-gray-800/80 border-gray-700 text-gray-100'
    : 'rounded-xl border p-3 min-w-0 bg-white/95 border-white/40 text-gray-900 shadow-lg';

  return (
    <div className={`min-h-screen overflow-x-hidden ${pageBg}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-5xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-4 sm:mb-6 text-sm ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={18} />
          Anasayfa
        </Link>

        <header
          className={`rounded-2xl border p-5 sm:p-8 mb-6 sm:mb-8 ${heroBg} ${
            darkMode ? 'text-white' : 'text-white'
          }`}
        >
          <div className="flex items-start gap-3 sm:gap-4">
            <div
              className={`shrink-0 p-3 rounded-xl ${
                darkMode ? 'bg-amber-500/20' : 'bg-white/15 backdrop-blur'
              }`}
            >
              <Trophy className="text-amber-300" size={32} aria-hidden />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Forum Ligi</h1>
              <p className={`mt-2 text-sm sm:text-base max-w-xl ${darkMode ? 'text-indigo-200' : 'text-indigo-100'}`}>
                Podyum, kategori şampiyonları ve lig kademeleri — anasayfadaki arama listesinden farklı,
                yarışma odaklı bir sıralama deneyimi.
              </p>
            </div>
          </div>

          {!loading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-5 sm:mt-6">
              <div className={statBox}>
                <Users size={18} className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                <p className="text-xs mt-1 opacity-80">Yarışan forum</p>
                <p className="text-lg sm:text-xl font-bold tabular-nums truncate">{stats.total}</p>
              </div>
              <div className={statBox}>
                <Layers size={18} className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                <p className="text-xs mt-1 opacity-80">Kategori</p>
                <p className="text-lg sm:text-xl font-bold tabular-nums truncate">{stats.categories}</p>
              </div>
              <div className={statBox}>
                <TrendingUp size={18} className="text-green-400" />
                <p className="text-xs mt-1 opacity-80">Büyüyen forum</p>
                <p className="text-lg sm:text-xl font-bold tabular-nums truncate">{stats.growingCount}</p>
              </div>
              <div className={statBox}>
                <Sparkles size={18} className="text-amber-400" />
                <p className="text-xs mt-1 opacity-80">Skor lideri</p>
                <p
                  className="text-lg sm:text-xl font-bold truncate"
                  title={stats.champion?.name}
                >
                  {stats.champion?.name ?? '—'}
                </p>
              </div>
            </div>
          )}
        </header>

        <div
          className={`sticky top-[52px] sm:top-[56px] z-20 -mx-3 px-3 sm:-mx-4 sm:px-4 py-2 mb-4 sm:mb-6 backdrop-blur ${
            darkMode ? 'bg-gray-900/90' : 'bg-gray-50/90'
          }`}
        >
          <div
            className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin"
            role="tablist"
            aria-label="Lig metriği"
          >
            {LEADER_METRICS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={metric === t.id}
                onClick={() => setMetric(t.id)}
                className={`shrink-0 px-4 py-2.5 rounded-full text-sm font-medium transition-colors min-h-[44px] ${
                  metric === t.id
                    ? 'bg-indigo-600 text-white shadow-md'
                    : darkMode
                      ? 'bg-gray-800 text-gray-200 border border-gray-600 hover:bg-gray-700'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-indigo-50'
                }`}
              >
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
          </div>
        ) : ranked.length === 0 ? (
          <div
            className={`text-center py-16 rounded-2xl border border-dashed ${
              darkMode ? 'border-gray-600 text-gray-400' : 'border-gray-300 text-gray-500'
            }`}
          >
            <TrendingUp className="mx-auto mb-3 opacity-50" size={40} />
            <p className="font-medium">Bu metrik için sıralanacak forum yok</p>
            <p className="text-sm mt-2 max-w-sm mx-auto">
              Büyüme sekmesinde yalnızca üye artışı olan forumlar listelenir. Önceki dönem verisi
              gerekir.
            </p>
          </div>
        ) : (
          <>
            <LeaderboardPodium topThree={topThree} metric={metric} darkMode={darkMode} />
            <CategoryChampions champions={champions} metric={metric} darkMode={darkMode} />
            <LeaderboardRankList entries={ranked} metric={metric} darkMode={darkMode} startRank={4} />

            <div
              className={`mt-8 sm:mt-10 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${
                darkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-indigo-50 border border-indigo-100'
              }`}
            >
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tüm forumları filtreleyip aramak için anasayfayı kullanın; lig sayfası özet ve
                kategori şampiyonlarına odaklanır.
              </p>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link
                  to="/"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium min-h-[44px] ${
                    darkMode
                      ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  <Search size={16} />
                  Forum ara
                </Link>
                <Link
                  to="/rising"
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border min-h-[44px] ${
                    darkMode
                      ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                      : 'border-indigo-200 text-indigo-700 hover:bg-white'
                  }`}
                >
                  <TrendingUp size={16} />
                  Yükselenler
                </Link>
              </div>
            </div>

            <p className={`text-xs text-center mt-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              S–A–B lig kademeleri: 1–10 S, 11–25 A, 26–50 B. Skor = (konu×5 + ileti×2 + üye×10) ÷ kuruluş
              yılı.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
